import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  BlockStack,
  InlineStack,
  Text,
  TextField,
  Checkbox,
  RangeSlider,
  Badge,
  Box,
  Select,
  Divider,
} from '@shopify/polaris';
import { MerchantPopupSettings } from '../types';

interface Props {
  settings: MerchantPopupSettings;
  onChange: (settings: MerchantPopupSettings) => void;
}

export function PopupSettingsForm({ settings, onChange }: Props) {
  const { t } = useTranslation();
  const update = useCallback(
    (patch: Partial<MerchantPopupSettings>) => onChange({ ...settings, ...patch }),
    [settings, onChange]
  );

  return (
    <BlockStack gap="400">
      <Card>
        <BlockStack gap="400">
          <InlineStack align="space-between">
            <Text variant="headingMd" as="h2">
              🔔 {t('popup.title')}
            </Text>
            <Badge tone={settings.enabled ? 'success' : 'new'}>
              {settings.enabled ? t('common.active') : t('common.inactive')}
            </Badge>
          </InlineStack>
          <Text as="p" tone="subdued">
            {t('popup.description')}
          </Text>
          <Checkbox
            label={t('popup.enableLabel')}
            checked={settings.enabled}
            onChange={(v) => update({ enabled: v })}
            helpText={t('popup.enableHelp')}
          />
        </BlockStack>
      </Card>

      {settings.enabled && (
        <>
          {/* Exit Intent */}
          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h2">{t('popup.exitIntent.title')}</Text>
              <Text as="p" tone="subdued">
                {t('popup.exitIntent.description')}
              </Text>

              <Checkbox
                label={t('popup.exitIntent.enableLabel')}
                checked={settings.exitIntentEnabled}
                onChange={(v) => update({ exitIntentEnabled: v })}
              />

              {settings.exitIntentEnabled && (
                <>
                  <TextField
                    label={t('popup.exitIntent.discountCodeLabel')}
                    value={settings.exitIntentDiscountCode || ''}
                    onChange={(v) => update({ exitIntentDiscountCode: v })}
                    helpText={t('popup.exitIntent.discountCodeHelp')}
                    autoComplete="off"
                    placeholder={t('popup.exitIntent.discountCodePlaceholder')}
                  />
                  <TextField
                    label={t('popup.exitIntent.messageLabel')}
                    value={settings.exitIntentMessage}
                    onChange={(v) => update({ exitIntentMessage: v })}
                    helpText={t('popup.exitIntent.messageHelp')}
                    autoComplete="off"
                    placeholder={t('popup.exitIntent.messagePlaceholder')}
                  />
                </>
              )}
            </BlockStack>
          </Card>

          {/* High Intent Upsell */}
          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h2">{t('popup.highIntent.title')}</Text>
              <Text as="p" tone="subdued">
                {t('popup.highIntent.description')}
              </Text>

              <Checkbox
                label={t('popup.highIntent.enableLabel')}
                checked={settings.highIntentUpsellEnabled}
                onChange={(v) => update({ highIntentUpsellEnabled: v })}
              />

              {settings.highIntentUpsellEnabled && (
                <TextField
                  label={t('popup.highIntent.minScoreLabel')}
                  type="number"
                  value={String(settings.highIntentMinScore)}
                  onChange={(v) => update({ highIntentMinScore: Number(v) })}
                  helpText={t('popup.highIntent.minScoreHelp')}
                  autoComplete="off"
                />
              )}
            </BlockStack>
          </Card>

          {/* Aggressiveness per Intent Level */}
          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h2">{t('popup.aggressiveness.title')}</Text>
              <Text as="p" tone="subdued">
                {t('popup.aggressiveness.description')}
              </Text>

              <Box paddingBlockStart="200">
                <BlockStack gap="400">
                  <RangeSlider
                    label={t('popup.aggressiveness.coldLabel', { value: settings.coldAggressiveness })}
                    value={settings.coldAggressiveness}
                    min={0}
                    max={100}
                    step={5}
                    output
                    onChange={(v) => update({ coldAggressiveness: v as number })}
                    helpText={t('popup.aggressiveness.coldHelp')}
                  />
                  <RangeSlider
                    label={t('popup.aggressiveness.warmLabel', { value: settings.warmAggressiveness })}
                    value={settings.warmAggressiveness}
                    min={0}
                    max={100}
                    step={5}
                    output
                    onChange={(v) => update({ warmAggressiveness: v as number })}
                    helpText={t('popup.aggressiveness.warmHelp')}
                  />
                  <RangeSlider
                    label={t('popup.aggressiveness.hotLabel', { value: settings.hotAggressiveness })}
                    value={settings.hotAggressiveness}
                    min={0}
                    max={100}
                    step={5}
                    output
                    onChange={(v) => update({ hotAggressiveness: v as number })}
                    helpText={t('popup.aggressiveness.hotHelp')}
                  />
                </BlockStack>
              </Box>
            </BlockStack>
          </Card>

          {/* Dynamic Product Counts */}
          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h2">{t('popup.dynamicCounts.title')}</Text>
              <Text as="p" tone="subdued">
                {t('popup.dynamicCounts.description')}
              </Text>

              <Checkbox
                label={t('popup.dynamicCounts.enableLabel')}
                checked={settings.dynamicProductCount}
                onChange={(v) => update({ dynamicProductCount: v })}
              />

              {settings.dynamicProductCount && (
                <InlineStack gap="400" wrap>
                  <TextField
                    label={t('popup.dynamicCounts.coldLabel')}
                    type="number"
                    value={String(settings.coldProductCount)}
                    onChange={(v) => update({ coldProductCount: Number(v) })}
                    helpText={t('popup.dynamicCounts.productsShownHelp')}
                    autoComplete="off"
                  />
                  <TextField
                    label={t('popup.dynamicCounts.warmLabel')}
                    type="number"
                    value={String(settings.warmProductCount)}
                    onChange={(v) => update({ warmProductCount: Number(v) })}
                    helpText={t('popup.dynamicCounts.productsShownHelp')}
                    autoComplete="off"
                  />
                  <TextField
                    label={t('popup.dynamicCounts.hotLabel')}
                    type="number"
                    value={String(settings.hotProductCount)}
                    onChange={(v) => update({ hotProductCount: Number(v) })}
                    helpText={t('popup.dynamicCounts.productsShownHelp')}
                    autoComplete="off"
                  />
                </InlineStack>
              )}
            </BlockStack>
          </Card>

          {/* Shipping Progress Bar */}
          <Card>
            <BlockStack gap="300">
              <Text variant="headingMd" as="h2">{t('popup.shippingBar.title')}</Text>
              <Checkbox
                label={t('popup.shippingBar.label')}
                checked={settings.intentShippingBar}
                onChange={(v) => update({ intentShippingBar: v })}
                helpText={t('popup.shippingBar.helpText')}
              />
            </BlockStack>
          </Card>

          {/* Discount System */}
          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h2">💰 {t('popup.discount.title', 'Discount System')}</Text>
              <Text as="p" tone="subdued">
                {t('popup.discount.description', 'Choose between a manual discount code or auto-generated discounts based on visitor intent score.')}
              </Text>

              <Select
                label={t('popup.discount.modeLabel', 'Discount mode')}
                options={[
                  { label: t('popup.discount.modeManual', 'Manual — use a fixed discount code'), value: 'manual' },
                  { label: t('popup.discount.modeAuto', 'Automatic — generate based on intent score'), value: 'auto' },
                ]}
                value={settings.discountMode}
                onChange={(v) => update({ discountMode: v as 'manual' | 'auto' })}
              />

              {settings.discountMode === 'manual' && (
                <TextField
                  label={t('popup.exitIntent.discountCodeLabel')}
                  value={settings.exitIntentDiscountCode || ''}
                  onChange={(v) => update({ exitIntentDiscountCode: v })}
                  helpText={t('popup.discount.manualHelp', 'Enter a discount code created in Shopify. It will be shown as-is in the popup.')}
                  autoComplete="off"
                  placeholder="SAVE10"
                />
              )}

              {settings.discountMode === 'auto' && (
                <BlockStack gap="400">
                  <Select
                    label={t('popup.discount.strategyLabel', 'Discount strategy')}
                    options={[
                      { label: t('popup.discount.strategyRescue', 'Rescue — higher discount for low-intent visitors (recommended)'), value: 'rescue' },
                      { label: t('popup.discount.strategyReward', 'Reward — higher discount for high-intent visitors'), value: 'reward' },
                    ]}
                    value={settings.autoDiscountStrategy}
                    onChange={(v) => update({ autoDiscountStrategy: v as 'rescue' | 'reward' })}
                    helpText={t('popup.discount.strategyHelp', 'Rescue mode gives bigger discounts to visitors about to leave. Reward mode gives bigger discounts to engaged visitors.')}
                  />

                  <InlineStack gap="400" wrap>
                    <TextField
                      label={t('popup.discount.minLabel', 'Min discount %')}
                      type="number"
                      value={String(settings.autoDiscountMin)}
                      onChange={(v) => update({ autoDiscountMin: Math.max(1, Math.min(99, Number(v))) })}
                      helpText={settings.autoDiscountStrategy === 'rescue'
                        ? t('popup.discount.minHelpRescue', 'Discount for high-intent visitors (they would buy anyway)')
                        : t('popup.discount.minHelp', 'Discount for low-intent visitors')}
                      autoComplete="off"
                      suffix="%"
                    />
                    <TextField
                      label={t('popup.discount.maxLabel', 'Max discount %')}
                      type="number"
                      value={String(settings.autoDiscountMax)}
                      onChange={(v) => update({ autoDiscountMax: Math.max(1, Math.min(99, Number(v))) })}
                      helpText={settings.autoDiscountStrategy === 'rescue'
                        ? t('popup.discount.maxHelpRescue', 'Discount for low-intent visitors (need convincing)')
                        : t('popup.discount.maxHelp', 'Discount for high-intent visitors')}
                      autoComplete="off"
                      suffix="%"
                    />
                  </InlineStack>

                  <TextField
                    label={t('popup.discount.capLabel', 'Maximum discount cap %')}
                    type="number"
                    value={String(settings.autoDiscountCap)}
                    onChange={(v) => update({ autoDiscountCap: Math.max(1, Math.min(50, Number(v))) })}
                    helpText={t('popup.discount.capHelp', 'Safety limit — no generated discount will ever exceed this percentage, regardless of the formula.')}
                    autoComplete="off"
                    suffix="%"
                  />

                  <InlineStack gap="400" wrap>
                    <TextField
                      label={t('popup.discount.durationLabel', 'Code valid for (minutes)')}
                      type="number"
                      value={String(settings.autoDiscountDurationMinutes)}
                      onChange={(v) => update({ autoDiscountDurationMinutes: Math.max(5, Number(v)) })}
                      helpText={t('popup.discount.durationHelp', 'How long the generated code stays active')}
                      autoComplete="off"
                      suffix="min"
                    />
                    <TextField
                      label={t('popup.discount.prefixLabel', 'Code prefix')}
                      value={settings.autoDiscountPrefix}
                      onChange={(v) => update({ autoDiscountPrefix: v.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8) })}
                      helpText={t('popup.discount.prefixHelp', 'E.g. SAVE → SAVE15-A3B2C1')}
                      autoComplete="off"
                      placeholder="SAVE"
                    />
                  </InlineStack>
                  <Box paddingBlockStart="200">
                    <Text as="p" tone="subdued">
                      {settings.autoDiscountStrategy === 'rescue'
                        ? t('popup.discount.rescueExplain', `Rescue mode: Low-intent visitors (about to leave) get up to ${settings.autoDiscountMax}% off. High-intent visitors get only ${settings.autoDiscountMin}% off. Capped at ${settings.autoDiscountCap}%.`)
                        : t('popup.discount.rewardExplain', `Reward mode: High-intent visitors get up to ${settings.autoDiscountMax}% off. Low-intent visitors get ${settings.autoDiscountMin}% off. Capped at ${settings.autoDiscountCap}%.`)}
                    </Text>
                  </Box>
                </BlockStack>
              )}
            </BlockStack>
          </Card>

          {/* Popup Cooldown & Safety */}
          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h2">⏱️ {t('popup.cooldown.title', 'Popup Cooldown & Safety')}</Text>
              <Text as="p" tone="subdued">
                {t('popup.cooldown.description', 'Prevent the popup from triggering too early. The visitor must meet ALL conditions below before the popup can appear.')}
              </Text>

              <InlineStack gap="400" wrap>
                <TextField
                  label={t('popup.cooldown.secondsLabel', 'Minimum time on site (seconds)')}
                  type="number"
                  value={String(settings.popupCooldownSeconds)}
                  onChange={(v) => update({ popupCooldownSeconds: Math.max(0, Number(v)) })}
                  helpText={t('popup.cooldown.secondsHelp', 'Visitor must spend at least this many seconds on site before the popup can trigger.')}
                  autoComplete="off"
                  suffix="s"
                />
                <TextField
                  label={t('popup.cooldown.pageViewsLabel', 'Minimum page views')}
                  type="number"
                  value={String(settings.popupMinPageViews)}
                  onChange={(v) => update({ popupMinPageViews: Math.max(1, Number(v)) })}
                  helpText={t('popup.cooldown.pageViewsHelp', 'Visitor must view at least this many pages before the popup can trigger.')}
                  autoComplete="off"
                  suffix="pages"
                />
              </InlineStack>
            </BlockStack>
          </Card>

          {/* Appearance */}
          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h2">🎨 {t('popup.appearance.title', 'Appearance')}</Text>
              <Text as="p" tone="subdued">
                {t('popup.appearance.description', 'Customize the look and feel of the popup.')}
              </Text>

              <TextField
                label={t('popup.appearance.popupTitleLabel', 'Custom popup title')}
                value={settings.popupTitle}
                onChange={(v) => update({ popupTitle: v })}
                helpText={t('popup.appearance.popupTitleHelp', 'Leave empty to use the default intent-based title.')}
                autoComplete="off"
                placeholder="Recommended for you!"
              />

              <Divider />

              {/* Colors */}
              <Text variant="headingSm" as="h3">{t('popup.appearance.colorsTitle', 'Colors')}</Text>
              <InlineStack gap="400" wrap>
                <Box>
                  <BlockStack gap="100">
                    <Text as="span" variant="bodySm">{t('popup.appearance.accentColorLabel', 'Accent color')}</Text>
                    <input
                      type="color"
                      value={settings.accentColor}
                      onChange={(e) => update({ accentColor: e.target.value })}
                      style={{ width: 48, height: 32, border: 'none', cursor: 'pointer', borderRadius: 4 }}
                    />
                    <Text as="span" variant="bodySm" tone="subdued">{settings.accentColor}</Text>
                  </BlockStack>
                </Box>
                <Box>
                  <BlockStack gap="100">
                    <Text as="span" variant="bodySm">{t('popup.appearance.bgColorLabel', 'Background')}</Text>
                    <input
                      type="color"
                      value={settings.backgroundColor}
                      onChange={(e) => update({ backgroundColor: e.target.value })}
                      style={{ width: 48, height: 32, border: 'none', cursor: 'pointer', borderRadius: 4 }}
                    />
                    <Text as="span" variant="bodySm" tone="subdued">{settings.backgroundColor}</Text>
                  </BlockStack>
                </Box>
                <Box>
                  <BlockStack gap="100">
                    <Text as="span" variant="bodySm">{t('popup.appearance.textColorLabel', 'Text color')}</Text>
                    <input
                      type="color"
                      value={settings.textColor}
                      onChange={(e) => update({ textColor: e.target.value })}
                      style={{ width: 48, height: 32, border: 'none', cursor: 'pointer', borderRadius: 4 }}
                    />
                    <Text as="span" variant="bodySm" tone="subdued">{settings.textColor}</Text>
                  </BlockStack>
                </Box>
              </InlineStack>

              <Divider />

              {/* Layout & Sizing */}
              <Text variant="headingSm" as="h3">{t('popup.appearance.layoutTitle', 'Layout & Sizing')}</Text>
              <InlineStack gap="400" wrap>
                <Select
                  label={t('popup.appearance.layoutLabel', 'Product layout')}
                  options={[
                    { label: t('popup.appearance.layoutList', 'List (vertical)'), value: 'list' },
                    { label: t('popup.appearance.layoutGrid', 'Grid (2 columns)'), value: 'grid' },
                  ]}
                  value={settings.layout}
                  onChange={(v) => update({ layout: v as 'list' | 'grid' })}
                />
                <Select
                  label={t('popup.appearance.animationLabel', 'Animation')}
                  options={[
                    { label: t('popup.appearance.animBounce', 'Bounce'), value: 'bounce' },
                    { label: t('popup.appearance.animSlide', 'Slide up'), value: 'slide' },
                    { label: t('popup.appearance.animFade', 'Fade in'), value: 'fade' },
                  ]}
                  value={settings.animation}
                  onChange={(v) => update({ animation: v as 'bounce' | 'slide' | 'fade' })}
                />
              </InlineStack>

              <RangeSlider
                label={t('popup.appearance.borderRadiusLabel', `Border radius: ${settings.borderRadius}px`)}
                value={settings.borderRadius}
                min={0}
                max={32}
                step={2}
                output
                onChange={(v) => update({ borderRadius: v as number })}
              />

              <RangeSlider
                label={t('popup.appearance.maxWidthLabel', `Max width: ${settings.maxWidth}px`)}
                value={settings.maxWidth}
                min={320}
                max={800}
                step={20}
                output
                onChange={(v) => update({ maxWidth: v as number })}
              />

              <RangeSlider
                label={t('popup.appearance.overlayLabel', `Overlay opacity: ${settings.overlayOpacity}%`)}
                value={settings.overlayOpacity}
                min={0}
                max={90}
                step={5}
                output
                onChange={(v) => update({ overlayOpacity: v as number })}
              />

              <Divider />

              {/* Product Images */}
              <Text variant="headingSm" as="h3">{t('popup.appearance.imagesTitle', 'Product Images')}</Text>
              <Checkbox
                label={t('popup.appearance.showImagesLabel', 'Show product images')}
                checked={settings.showProductImages}
                onChange={(v) => update({ showProductImages: v })}
              />
              {settings.showProductImages && (
                <RangeSlider
                  label={t('popup.appearance.imageSizeLabel', `Image size: ${settings.imageSize}px`)}
                  value={settings.imageSize}
                  min={32}
                  max={128}
                  step={8}
                  output
                  onChange={(v) => update({ imageSize: v as number })}
                />
              )}

              <Divider />

              {/* Extra options */}
              <Text variant="headingSm" as="h3">{t('popup.appearance.extrasTitle', 'Extra Options')}</Text>
              <Checkbox
                label={t('popup.appearance.showBrowseLabel', 'Show "Browse more" link')}
                checked={settings.showBrowseLink}
                onChange={(v) => update({ showBrowseLink: v })}
              />
              {settings.showBrowseLink && (
                <TextField
                  label={t('popup.appearance.browseLinkTextLabel', 'Browse link text')}
                  value={settings.browseLinkText}
                  onChange={(v) => update({ browseLinkText: v })}
                  autoComplete="off"
                  placeholder="Browse more products"
                />
              )}
              <Checkbox
                label={t('popup.appearance.showBrandingLabel', 'Show NexusAI branding')}
                checked={settings.showBranding}
                onChange={(v) => update({ showBranding: v })}
              />
            </BlockStack>
          </Card>
        </>
      )}
    </BlockStack>
  );
}
